/**
 * Keeps the app's height equal to the *visible* area, so the soft keyboard
 * shrinks the layout instead of shoving it off-screen.
 *
 * Why this is needed at all: the app sizes itself with
 * `--layout-viewport-height`, which defaults to `100vh`. On Android `100vh` is
 * the LARGE viewport — it deliberately ignores the soft keyboard, so when the
 * IME opens the layout stays full height and the system pans the whole window
 * upward. `adjustResize` in the manifest used to prevent that, but Android 15's
 * edge-to-edge enforcement ignores it, and opting out of edge-to-edge is not
 * something we want to do.
 *
 * `visualViewport` reports the area actually visible to the user, keyboard
 * excluded, on every platform that has one — so we drive the height from it and
 * publish the keyboard's height alongside for anything pinned to the bottom.
 */

/** Height of the soft keyboard, or 0 when it is closed. */
export const KEYBOARD_INSET_VAR = '--keyboard-inset-height';
export const VIEWPORT_HEIGHT_VAR = '--layout-viewport-height';

/**
 * Start syncing the CSS variables to the visual viewport. Returns a teardown.
 * A no-op (leaving the `100vh` default in place) where visualViewport is
 * missing, so unsupported browsers keep today's behaviour rather than breaking.
 */
export const startViewportHeightSync = (): (() => void) => {
  const vv = typeof window !== 'undefined' ? window.visualViewport : undefined;
  if (!vv) return () => undefined;

  const root = document.documentElement;

  const apply = () => {
    // The layout viewport does not shrink for the keyboard; the visual one
    // does. Their difference is the keyboard (plus any pan the OS applied).
    const keyboard = Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop));

    root.style.setProperty(VIEWPORT_HEIGHT_VAR, `${Math.round(vv.height)}px`);
    root.style.setProperty(KEYBOARD_INSET_VAR, `${keyboard}px`);

    // With the app exactly as tall as the visible area there is nothing left to
    // scroll, but a pan already in progress has to be undone or the top of the
    // app stays pushed off-screen.
    if (keyboard > 0 && window.scrollY !== 0) window.scrollTo(0, 0);
  };

  apply();
  vv.addEventListener('resize', apply);
  vv.addEventListener('scroll', apply);
  window.addEventListener('orientationchange', apply);

  return () => {
    vv.removeEventListener('resize', apply);
    vv.removeEventListener('scroll', apply);
    window.removeEventListener('orientationchange', apply);
    root.style.removeProperty(VIEWPORT_HEIGHT_VAR);
    root.style.removeProperty(KEYBOARD_INSET_VAR);
  };
};
